import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { UserProfile } from '../types';
import CarouselManagementTab from './CarouselManagementTab';
import PortfolioManagementTab from './PortfolioManagementTab';
import {
    LayoutGridIcon,
    SlidersHorizontalIcon,
    XIcon,
    UsersIcon,
    BotIcon,
    BarChart3Icon,
    ShieldCheckIcon,
    ActivityIcon,
    SearchIcon,
    UserCheckIcon,
    AlertCircleIcon,
    ZapIcon
} from './icons';
import { MEMBERSHIP_LIMITS, SYSTEM_AGENTS } from '../constants';
import { isSystemAgentActive } from '../services/agentService';

interface AdminManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    allUserProfiles: { userId: string, profile: UserProfile }[];
}

const AdminManagementModal: React.FC<AdminManagementModalProps> = ({ isOpen, onClose, allUserProfiles }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'users' | 'agents' | 'overview' | 'settings' | 'carousel' | 'portfolio'>('overview');
    const [userSearch, setUserSearch] = useState('');
    const [emailProvider, setEmailProvider] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('codina_email_provider') || 'emailjs';
        }
        return 'emailjs';
    });

    const handleEmailProviderChange = (provider: string) => {
        setEmailProvider(provider);
        if (typeof window !== 'undefined') {
            localStorage.setItem('codina_email_provider', provider);
        }
    };

    // Site Stats Calculation
    const stats = useMemo(() => {
        const totalUsers = allUserProfiles.length;
        const activeMemberships = allUserProfiles.filter(p => (p.profile?.membershipLevel || 'basic') !== 'free').length;
        const totalGenerations = allUserProfiles.reduce((acc, curr) => acc + (curr.profile?.generationCount || 0), 0);
        const activeAgentsCount = allUserProfiles.reduce((acc, curr) => acc + (curr.profile?.styleAgents?.length || 0), 0);

        return { totalUsers, activeMemberships, totalGenerations, activeAgentsCount };
    }, [allUserProfiles]);

    const filteredUsers = useMemo(() => {
        return allUserProfiles.filter(u =>
            u.userId.toLowerCase().includes(userSearch.toLowerCase()) ||
            (u.profile?.email || '').toLowerCase().includes(userSearch.toLowerCase())
        );
    }, [allUserProfiles, userSearch]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="relative w-full max-w-5xl h-[80vh] bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-600/20">
                                <ShieldCheckIcon className="w-7 h-7" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white">Admin Control Center</h2>
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-widest text-indigo-600 dark:text-indigo-400">Site Management & Monitoring</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-400"
                            title="Close"
                        >
                            <XIcon className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex border-b border-gray-100 dark:border-gray-800 px-6 bg-white dark:bg-gray-900">
                        {[
                            { id: 'overview', icon: BarChart3Icon, label: 'Overview' },
                            { id: 'users', icon: UsersIcon, label: 'User Management' },
                            { id: 'agents', icon: BotIcon, label: 'Agent Monitor' },
                            { id: 'carousel', icon: LayoutGridIcon, label: 'Home Data' },
                            { id: 'portfolio', icon: SlidersHorizontalIcon, label: 'Portfolio Data' },
                            { id: 'settings', icon: ShieldCheckIcon, label: 'System Settings' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-all text-sm font-bold ${activeTab === tab.id
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-gray-400 hover:text-gray-600'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-gray-50/30 dark:bg-gray-900/30">
                        <AnimatePresence mode="wait">
                            {activeTab === 'overview' && (
                                <motion.div
                                    key="overview"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    className="space-y-8"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                        {[
                                            { label: 'Total Users', value: stats.totalUsers, icon: UsersIcon, color: 'indigo' },
                                            { label: 'Active Members', value: stats.activeMemberships, icon: UserCheckIcon, color: 'emerald' },
                                            { label: 'Cloud Generations', value: stats.totalGenerations, icon: ZapIcon, color: 'amber' },
                                            { label: 'Active Agents', value: stats.activeAgentsCount, icon: BotIcon, color: 'rose' }
                                        ].map(stat => (
                                            <div key={stat.label} className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col justify-between">
                                                <div className="flex items-start justify-between">
                                                    <span className={`p-2 rounded-xl bg-${stat.color}-50 dark:bg-${stat.color}-900/30 text-${stat.color}-600 dark:text-${stat.color}-400`}>
                                                        <stat.icon className="w-6 h-6" />
                                                    </span>
                                                    <ActivityIcon className="w-4 h-4 text-gray-200" />
                                                </div>
                                                <div className="mt-4">
                                                    <p className="text-3xl font-bold dark:text-white">{stat.value}</p>
                                                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{stat.label}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* System Health / Trends Placeholder */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="text-xl font-bold dark:text-white flex items-center gap-2">
                                                <ActivityIcon className="w-5 h-5 text-indigo-500" />
                                                System Performance Trends
                                            </h3>
                                            <span className="text-xs font-bold px-3 py-1 bg-green-100 text-green-700 rounded-full uppercase tracking-tighter">Healthy</span>
                                        </div>
                                        <div className="h-48 flex items-end gap-2 px-2">
                                            {[40, 70, 45, 90, 65, 80, 55, 95, 30, 85].map((h, i) => (
                                                <motion.div
                                                    key={i}
                                                    initial={{ height: 0 }}
                                                    animate={{ height: `${h}%` }}
                                                    transition={{ delay: i * 0.05 }}
                                                    className="flex-1 bg-gradient-to-t from-indigo-600/20 to-indigo-600 rounded-t-lg"
                                                />
                                            ))}
                                        </div>
                                        <div className="flex justify-between mt-4 text-[10px] text-gray-400 font-bold uppercase">
                                            <span>24 Hours Ago</span>
                                            <span>Now</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {activeTab === 'users' && (
                                <motion.div
                                    key="users"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    className="space-y-6"
                                >
                                    <div className="relative">
                                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search by ID or email..."
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl py-4 pl-12 pr-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-600/20 transition-all shadow-sm"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4">
                                        {filteredUsers.map(({ userId, profile }) => {
                                            const level = (profile?.membershipLevel || 'basic') as keyof typeof MEMBERSHIP_LIMITS;
                                            const limit = MEMBERSHIP_LIMITS[level] || MEMBERSHIP_LIMITS['basic'];
                                            const usagePercent = Math.min((profile?.generationCount || 0) / limit * 100, 100);

                                            return (
                                                <div key={userId} className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col md:flex-row md:items-center gap-6">
                                                    <div className="flex items-center gap-4 flex-1">
                                                        <div className="w-12 h-12 rounded-full bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 font-bold text-lg">
                                                            {userId.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-gray-900 dark:text-white truncate">{profile?.email || userId}</p>
                                                            <p className="text-[10px] text-gray-400 font-mono">UID: {userId}</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex-1 space-y-2">
                                                        <div className="flex justify-between text-[10px] font-bold uppercase">
                                                            <span className="text-gray-500">Quota Usage</span>
                                                            <span className={usagePercent > 80 ? 'text-rose-500' : 'text-indigo-500'}>
                                                                {profile?.generationCount || 0} / {limit}
                                                            </span>
                                                        </div>
                                                        <div className="h-2 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${usagePercent}%` }}
                                                                className={`h-full rounded-full ${usagePercent > 80 ? 'bg-rose-500' : 'bg-indigo-600'}`}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-3">
                                                        <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${level === 'advanced' ? 'bg-amber-100 text-amber-700' :
                                                            level === 'premium' ? 'bg-indigo-100 text-indigo-700' :
                                                                'bg-gray-100 text-gray-600'
                                                            }`}>
                                                            {level}
                                                        </div>
                                                        <span className={`w-3 h-3 rounded-full ${profile?.accountStatus === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}

                            {activeTab === 'agents' && (
                                <motion.div
                                    key="agents"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                                >
                                    {SYSTEM_AGENTS.map(agent => (
                                        <div key={agent.id} className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm relative overflow-hidden group">
                                            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-600" />
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
                                                    <BotIcon className="w-6 h-6" />
                                                </div>
                                                <div className={`px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-tighter ${isSystemAgentActive(agent.id) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                    }`}>
                                                    {isSystemAgentActive(agent.id) ? 'Active' : 'Halted'}
                                                </div>
                                            </div>
                                            <h3 className="font-bold text-gray-900 dark:text-white capitalize mb-1">{t(agent.nameKey)}</h3>
                                            <p className="text-xs text-gray-400 font-mono mb-4">ID: {agent.id}</p>

                                            <div className="pt-4 border-t border-gray-50 dark:border-gray-700 flex items-center justify-between">
                                                <div className="flex items-center gap-1.5">
                                                    <ShieldCheckIcon className="w-3.5 h-3.5 text-gray-400" />
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Requires {agent.requiredMembership || 'free'}</span>
                                                </div>
                                                <ActivityIcon className="w-4 h-4 text-green-500 animate-pulse" />
                                            </div>
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                            
                            {activeTab === 'carousel' && (
                                <motion.div
                                    key="carousel"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    className="space-y-8"
                                >
                                    <CarouselManagementTab />
                                </motion.div>
                            )}
                            
                            {activeTab === 'portfolio' && (
                                <motion.div
                                    key="portfolio"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    className="space-y-6"
                                >
                                    <PortfolioManagementTab />
                                </motion.div>
                            )}

                            {activeTab === 'settings' && (
                                <motion.div
                                    key="settings"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    className="space-y-8"
                                >
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <h3 className="text-xl font-bold dark:text-white flex items-center gap-2 mb-6">
                                            <ShieldCheckIcon className="w-5 h-5 text-indigo-500" />
                                            Email Service Configuration
                                        </h3>
                                        
                                        <div className="space-y-4 max-w-xl">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Select the background service used to send platform emails (e.g., booking confirmations, system alerts).
                                            </p>
                                            
                                            <div className="flex gap-4">
                                                <label className={`flex-1 border rounded-xl p-4 cursor-pointer transition-all ${emailProvider === 'emailjs' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <input 
                                                            type="radio" 
                                                            name="email_provider" 
                                                            value="emailjs" 
                                                            checked={emailProvider === 'emailjs'}
                                                            onChange={(e) => handleEmailProviderChange(e.target.value)}
                                                            className="text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <span className="font-bold text-gray-900 dark:text-white">EmailJS (Default)</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 ml-5">Standard reliable delivery using pre-configured EmailJS templates.</p>
                                                </label>
                                                
                                                <label className={`flex-1 border rounded-xl p-4 cursor-pointer transition-all ${emailProvider === 'gmail api' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <input 
                                                            type="radio" 
                                                            name="email_provider" 
                                                            value="gmail api" 
                                                            checked={emailProvider === 'gmail api'}
                                                            onChange={(e) => handleEmailProviderChange(e.target.value)}
                                                            className="text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <span className="font-bold text-gray-900 dark:text-white">Gmail API</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 ml-5">Direct delivery via Google Workspace (Requires full valid OAuth tokens).</p>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Footer Info */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/80 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center px-8">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            <ShieldCheckIcon className="w-3 h-3" />
                            <span>Encrypted Administrative Session</span>
                        </div>
                        <div className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">
                            v2.1.0-A2UI
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default AdminManagementModal;
